import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Loader2, X, Check, User } from 'lucide-react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/**
 * Reusable EmployeeAutocomplete Component across the entire application.
 *
 * Fixes & Enhancements:
 * - Real-time client-side + server-side filtering
 * - Request sequence versioning (reqVersionRef) preventing stale background fetches from overwriting results after 1s
 * - Results stay filtered until the user manually changes or clears the search query
 * - Supports searching by: Employee ID, Employee Code, Name, Department, Designation, Mobile, Email
 */
export default function EmployeeAutocomplete({
  companyId,
  value, // single ID string
  selected = [], // array of ID strings for multi-select
  multiple = false,
  onChange,
  placeholder = 'Search by name, code (e.g. EMP001), dept, email…',
  disabled = false,
  className = '',
  'data-testid': testId = 'employee-autocomplete',
}) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const wrapperRef = useRef(null);

  // Request sequence versioning to eliminate race conditions
  const reqVersionRef = useRef(0);

  // Normalize selected IDs
  const activeIds = useMemo(() => {
    return multiple ? (Array.isArray(selected) ? selected : []) : (value ? [value] : []);
  }, [multiple, selected, value]);

  const activeIdsStr = activeIds.join(',');

  // Fetch employee options with version tracking to ignore stale responses
  const fetchOptions = useCallback(async (searchQuery = '') => {
    const currentVersion = ++reqVersionRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.set('q', searchQuery.trim());
      if (companyId) params.set('company_id', companyId);
      params.set('limit', '100');

      const res = await axios.get(`${API_BASE}/employees/lookup?${params.toString()}`, { withCredentials: true });
      
      // Discard response if a newer search request was launched
      if (currentVersion !== reqVersionRef.current) {
        return;
      }

      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setOptions(items);
    } catch (err) {
      if (currentVersion === reqVersionRef.current) {
        console.error('EmployeeAutocomplete fetch failed:', err);
        setOptions([]);
      }
    } finally {
      if (currentVersion === reqVersionRef.current) {
        setLoading(false);
      }
    }
  }, [companyId]);

  // Pre-load details for selected IDs so chips/selected text render correctly
  useEffect(() => {
    if (!activeIdsStr) {
      setSelectedDetails([]);
      return;
    }
    const ids = activeIdsStr.split(',').filter(Boolean);
    const missingIds = ids.filter(id => !selectedDetails.some(d => (d.id || d._id) === id));
    if (missingIds.length === 0) return;

    let isMounted = true;
    (async () => {
      try {
        const fetchedDocs = await Promise.all(
          missingIds.map(id =>
            axios.get(`${API_BASE}/employees/${id}`, { withCredentials: true })
              .then(r => r.data)
              .catch(() => null)
          )
        );
        if (isMounted) {
          const validDocs = fetchedDocs.filter(Boolean);
          if (validDocs.length > 0) {
            setSelectedDetails(prev => {
              const existingMap = new Map(prev.map(item => [item.id || item._id, item]));
              validDocs.forEach(doc => {
                const docId = doc.id || doc._id;
                if (docId) existingMap.set(docId, doc);
              });
              return Array.from(existingMap.values());
            });
          }
        }
      } catch (e) {
        // ignore fallback
      }
    })();
    return () => { isMounted = false; };
  }, [activeIdsStr]);

  // Debounced search when typing
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      fetchOptions(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, isOpen, fetchOptions]);

  // Client-side real-time filtering fallback on options list
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;

    return options.filter(emp => {
      const code = (emp.employee_code || emp.employment_details?.employee_code || '').toLowerCase();
      const firstName = (emp.first_name || emp.personal_details?.first_name || '').toLowerCase();
      const lastName = (emp.last_name || emp.personal_details?.last_name || '').toLowerCase();
      const fullName = (emp.full_name || emp.name || emp.personal_details?.full_name || `${firstName} ${lastName}`).toLowerCase();
      const dept = (emp.department_name || emp.department || emp.employment_details?.department_name || emp.employment_details?.department || '').toLowerCase();
      const desig = (emp.designation_name || emp.designation || emp.employment_details?.designation_name || emp.employment_details?.designation || '').toLowerCase();
      const empId = String(emp.id || emp._id || '').toLowerCase();
      const mobile = (emp.mobile || emp.personal_details?.mobile || '').toLowerCase();
      const email = (emp.email || emp.work_email || emp.employment_details?.work_email || '').toLowerCase();

      return (
        code.includes(q) ||
        fullName.includes(q) ||
        firstName.includes(q) ||
        lastName.includes(q) ||
        dept.includes(q) ||
        desig.includes(q) ||
        empId.includes(q) ||
        mobile.includes(q) ||
        email.includes(q)
      );
    });
  }, [options, query]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (emp) => {
    const empId = emp.id || emp._id;
    if (multiple) {
      const isSelected = activeIds.includes(empId);
      const newIds = isSelected ? activeIds.filter(i => i !== empId) : [...activeIds, empId];
      const newDetails = isSelected ? selectedDetails.filter(d => (d.id || d._id) !== empId) : [...selectedDetails, emp];
      setSelectedDetails(newDetails);
      onChange && onChange(newIds, newDetails);
    } else {
      setSelectedDetails([emp]);
      onChange && onChange(empId, emp);
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleRemove = (empId, e) => {
    e.stopPropagation();
    if (multiple) {
      const newIds = activeIds.filter(i => i !== empId);
      const newDetails = selectedDetails.filter(d => (d.id || d._id) !== empId);
      setSelectedDetails(newDetails);
      onChange && onChange(newIds, newDetails);
    } else {
      setSelectedDetails([]);
      onChange && onChange('', null);
    }
  };

  const singleSelectedEmp = !multiple && activeIds.length > 0
    ? (selectedDetails.find(d => (d.id || d._id) === activeIds[0]) || options.find(d => (d.id || d._id) === activeIds[0]))
    : null;

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`} data-testid={testId}>
      {/* Selection Display & Input Trigger */}
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className={`min-h-[38px] w-full border border-slate-300 rounded-md bg-white px-3 py-1.5 flex flex-wrap items-center gap-1.5 cursor-text text-sm transition-colors ${
          isOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : 'hover:border-slate-400'
        } ${disabled ? 'opacity-60 bg-slate-50 cursor-not-allowed' : ''}`}
      >
        {/* Multi-select Chips */}
        {multiple && activeIds.map(id => {
          const detail = selectedDetails.find(d => (d.id || d._id) === id);
          const code = detail?.employment_details?.employee_code || detail?.employee_code || id.slice(0, 6);
          const name = detail?.full_name || detail?.name || `${detail?.personal_details?.first_name || ''} ${detail?.personal_details?.last_name || ''}`.trim() || 'Employee';
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium rounded-full px-2.5 py-0.5"
            >
              <span className="font-mono text-[10px] opacity-75">{code}</span>
              <span>·</span>
              <span>{name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => handleRemove(id, e)}
                  className="hover:bg-blue-200 rounded-full p-0.5 text-blue-600 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          );
        })}

        {/* Single-select Active Value Display */}
        {!multiple && singleSelectedEmp && !isOpen && (
          <div className="flex-1 flex items-center justify-between min-w-0">
            <div className="flex items-center gap-2 truncate">
              {singleSelectedEmp.photoUrl || singleSelectedEmp.photo ? (
                <img
                  src={singleSelectedEmp.photoUrl || singleSelectedEmp.photo}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-bold shrink-0">
                  <User className="w-3 h-3" />
                </div>
              )}
              <span className="truncate font-medium text-slate-800">
                {singleSelectedEmp.display_label || `${singleSelectedEmp.employee_code || singleSelectedEmp.employment_details?.employee_code} - ${singleSelectedEmp.full_name || singleSelectedEmp.name} - ${singleSelectedEmp.department_name || singleSelectedEmp.department || 'General'}`}
              </span>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => handleRemove(singleSelectedEmp.id || singleSelectedEmp._id, e)}
                className="text-slate-400 hover:text-slate-600 ml-2"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Live Search Input */}
        {(isOpen || (multiple && activeIds.length === 0) || (!multiple && !singleSelectedEmp)) && (
          <div className="flex-1 flex items-center min-w-[120px]">
            <Search className="w-3.5 h-3.5 text-slate-400 mr-1.5 shrink-0" />
            <input
              type="text"
              value={query}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                if (options.length === 0) fetchOptions(query);
              }}
              placeholder={placeholder}
              className="w-full bg-transparent outline-none text-slate-800 placeholder:text-slate-400 text-sm"
              data-testid={`${testId}-input`}
            />
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 shrink-0 ml-1" />}
          </div>
        )}
      </div>

      {/* Autocomplete Dropdown Popup */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto py-1"
          data-testid={`${testId}-dropdown`}
        >
          {loading && filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span>Searching employee master…</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-500 italic">
              No matching employees found in tenant database.
            </div>
          ) : (
            filteredOptions.map((emp) => {
              const empId = emp.id || emp._id;
              const isSelected = activeIds.includes(empId);
              const code = emp.employee_code || emp.employment_details?.employee_code || '';
              const name = emp.full_name || emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
              const dept = emp.department_name || emp.department || emp.employment_details?.department_name || 'General';
              const desig = emp.designation_name || emp.designation || emp.employment_details?.designation_name || 'Staff';
              const photo = emp.photoUrl || emp.photo || emp.personal_details?.photoUrl;

              return (
                <button
                  key={empId}
                  type="button"
                  onClick={() => handleSelect(emp)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                    isSelected ? 'bg-blue-50/70 text-blue-900 font-medium' : 'hover:bg-slate-50 text-slate-700'
                  }`}
                  data-testid={`${testId}-option-${code || empId}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {photo ? (
                      <img src={photo} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200 shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center font-semibold text-xs shrink-0">
                        {name ? name.charAt(0).toUpperCase() : 'E'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-900 truncate">{name}</span>
                        {code && (
                          <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                            {code}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">
                        <span>{dept}</span>
                        <span className="mx-1">·</span>
                        <span>{desig}</span>
                      </div>
                    </div>
                  </div>

                  {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export { EmployeeAutocomplete as EmployeeSelect };
